import type { Schema as JSONSchema } from '@cfworker/json-schema';
import type { MessageBlueprint as RcsMessageBlueprint } from 'components/rcsMessage/types';
import { MessageDirectionKeys } from 'components/sales/pages/types';
import type { SnakeCasedPropertiesDeep } from 'utils/caseConversion';
import { FeedbackSummary } from '../components/automations/sampleMessages/types';
import { AsyncJobStatusResponse } from '../utils/asyncJobs';

/**
 * Status values for reasoning/pipeline steps in Shopper
 */
export const ReasoningStepStatus = {
  UPCOMING: 'upcoming',
  ACTIVE: 'active',
  ENDED: 'ended',
} as const;

export type ReasoningStepStatus =
  typeof ReasoningStepStatus[keyof typeof ReasoningStepStatus];

/**
 * A single step in the Shopper pipeline processing with status
 */
export interface PipelineStep {
  name: string;
  reasoning: {
    upcoming: string;
    active: string;
    ended: string;
  };
  status: ReasoningStepStatus;
}

/**
 * Progress information for Shopper pipeline processing
 */
/* eslint-disable camelcase */
export interface PipelineProgress {
  steps: PipelineStep[];
  current_attempt: number;
  max_attempts: number;
  retry_reason: string | null;
}
/* eslint-enable camelcase */

export enum DiscountType {
  SHOPIFY_DISCOUNT_CODE = 'shopify-discount-code',
  FONDUE_CASHBACK_URL = 'fondue-cashback-promotion',
}

export const HandoffProviderIds = {
  GORGIAS: 'gorgias',
  ZENDESK: 'zendesk',
} as const;

export type HandoffProviderId =
  | typeof HandoffProviderIds[keyof typeof HandoffProviderIds]
  | (string & NonNullable<unknown>);

export interface HandoffProvider {
  providerType: HandoffProviderId;
  label: string;
  isIntegrationConnected: boolean;
  isAutoForwardActive: boolean;
}

export interface HandoffProvidersResponse {
  providers: HandoffProvider[];
  configuredProviderType: HandoffProviderId | null;
  effectiveProviderType: HandoffProviderId | null;
}

/* eslint-disable camelcase */
export interface ConfigValues {
  isLiveMode: boolean;
  discountType: DiscountType;
  discountCode?: string;
  discountFondueUrl?: string;
  discountDescription?: string;
  discountFondueDescription?: string;
  excludedProductTags: string[];
  fallbackSMS: string;
  handoffTransitionMessage: string;
  supportEmail: string;
  supportSMS: string;
  supportHandoffEnabled: boolean;
  supportHandoffProvider: HandoffProviderId | null;
  outreachMessages: OutreachMessage[];
  _strategicOffersValidation?: () => string; // Dummy field for strategic offers validation
}

export interface ConfigDataMetadata {
  created_at?: string;
  first_go_live_at?: string;
  last_go_live_at?: string;
  last_deactivated_at?: string;
}

export interface ConfigData {
  created_at?: string;
  discount_code?: string;
  discount_description?: string;
  excluded_product_tags: string[];
  fallback_sms?: string;
  handoff_transition_message?: string | null;
  metadata?: ConfigDataMetadata;
  support_email: string;
  support_sms: string;
  is_live_mode: boolean;
  support_handoff_enabled?: boolean | null;
  support_handoff_provider?: HandoffProviderId | null;
  outreach_messages: OutreachMessage[];
  outreach_messages_modified?: boolean;
  exclusion_tags_modified?: boolean;
}

export interface Response {
  message: string;
}

export interface ShopperProductsStatusResponse {
  count_products_total: number;
  count_products_truncated: number;
  count_variants_truncated: number;
  is_truncated: boolean;
  has_full_product_access: boolean;
}

export interface ShopperProductsStatus {
  countProductsTotal: number;
  countProductsTruncated: number;
  countVariantsTruncated: number;
  isTruncated: boolean;
  hasFullProductAccess: boolean;
}

export enum ShopPromptOverridesProducts {
  SHOPPER_1_0 = 'Shopper 1.0',
  SHOPPER_2_0 = 'Shopper 2.0',
}

export interface ShopperPromptOverrides {
  shop_id: number;
  created_at: string;
  updated_at: string;
  last_updated_by: number;
  enabled: boolean;
  champion_prompt_name: string;
  champion_prompt_environment: ShopperPromptOverrideEnvironment;
  contender_prompt_enabled: boolean;
  contender_prompt_name: string;
  contender_prompt_environment: ShopperPromptOverrideEnvironment;
  contender_shadow_percentage: number;
  contender_rollout_percentage: number;
  product: string;
  langfuse_champion_prompt_name?: string | null;
  langfuse_champion_prompt_label?: LangfusePromptLabel | null;
  langfuse_contender_prompt_name?: string | null;
  langfuse_contender_prompt_label?: LangfusePromptLabel | null;
}

export interface ShopPromptDictResponse {
  shopper_1_0: ShopperPromptOverrides;
  shopper_2_0: ShopperPromptOverrides;
}

export interface UpdateShopperPromptOverrides {
  enabled: boolean;
  contender_prompt_enabled: boolean;
  contender_shadow_percentage: number;
  contender_rollout_percentage: number;
  product: string;
  langfuse_champion_prompt_name: string;
  langfuse_champion_prompt_label: LangfusePromptLabel;
  langfuse_contender_prompt_name: string | null;
  langfuse_contender_prompt_label: LangfusePromptLabel | null;
}

export const ShopperPromptOverrideEnvironments = {
  CHAMPION: 'champion',
  CONTENDER: 'contender',
};

export type ShopperPromptOverrideEnvironment =
  typeof ShopperPromptOverrideEnvironments[keyof typeof ShopperPromptOverrideEnvironments];

export const LangfusePromptLabels = {
  CHAMPION: 'champion',
  CONTENDER: 'contender',
} as const;

export type LangfusePromptLabel =
  typeof LangfusePromptLabels[keyof typeof LangfusePromptLabels];

export interface ShopperPromptOverridesResponse {
  data: ShopPromptDictResponse | null;
  message: string | null;
}

export interface Prompt {
  env: string;
  instructions: string;
  name: string | undefined;
  tokens?: number;
}

export interface ShopperDebugActivePromptsResponse {
  prompts: Prompt[];
  message?: string;
}

/** Metadata that describes a single resolver) */
export interface ResolverDefinition {
  /** Unique identifier used by the backend (e.g. "shop_name") */
  name: string;
  /** Human-readable label to show in the UI */
  label: string;
  /** Short description of what the resolver produces */
  description: string;
  /** JSON Schema for form fields — includes both resolver inputs and overridable execution params */
  input_schema: JSONSchema;
}

/** Top-level payload returned by `useContextResolverDefinitions` */
export interface ContextResolverDefinitionsResponse {
  resolvers: ResolverDefinition[];
}

export interface ExecuteContextResolverResponse {
  name: string;
  result: unknown;
}

export enum RegenerationStatus {
  NOT_REQUIRED = 'not_required',
  AWAITING_USER_CONFIRMATION = 'awaiting_user_confirmation',
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

// Types for message categories
export type SSAIMessageCategory =
  | 'question.discovery.shopping'
  | 'question.discovery.objection'
  | 'question.offer.proposal'
  | 'question.offer.nudge'
  | 'question.offer.reminder';

// Enums for message and batch statuses
export enum OutreachMessageStatus {
  APPROVED = 'approved',
  DESELECTED = 'deselected',
  DELETED = 'deleted',
  PENDING = 'pending',
}

export enum OutreachMessageBatchStatus {
  ACTIVE = 'active',
  UNREVIEWED = 'unreviewed',
}

// Interface for batch data
export interface OutreachMessageBatchData {
  [key: string]: unknown;
}

/**
 * Represents a batch of outreach messages
 */
export interface OutreachMessageBatch {
  id: number;
  status: OutreachMessageBatchStatus;
  data: OutreachMessageBatchData;
  created_at: string;
  updated_at: string;
  messages: OutreachMessage[];
}

/**
 * Represents a list of outreach messages for selection updates
 */
export interface OutreachMessageList {
  outreach_messages: OutreachMessage[];
}

/**
 * Represents an outreach message in the system
 */
export interface OutreachMessage {
  id: number;
  body: string;
  category: SSAIMessageCategory;
  status: OutreachMessageStatus;
  created_at: string;
  updated_at: string;
}

export interface AssignProductPlanFreeTrialResponse {
  message: string;
}

export type PipelineTaskStatus =
  | 'SKIPPED'
  | 'ERROR'
  | 'FAILED'
  | 'PASSED'
  | 'FIXED';

export type ShopperAgentRouteType = 'support' | 'spam' | 'attack';

/**
 * Result of a single pipeline task execution.
 * Extends PipelineTaskOutcome with task metadata.
 */
export interface PipelineTaskRunResult {
  type: 'PipelineTaskRunResult';
  name: string;
  display_name: string | null;
  description: string | null;

  shadow: boolean;
  blocking: boolean;
  skipped: boolean;
  can_modify_context: boolean;

  status: PipelineTaskStatus;
  fix_instructions: string | null;
  fixed_message: string | null;
  info: string | null;
  exc: string | null;
  should_continue: boolean;
  data: unknown; // Task-specific structured data
}

export interface PipelineTaskChainResult {
  name: string;
  display_name: string | null;
  description: string | null;

  passed: boolean;
  should_continue: boolean;
  task_results: PipelineTaskRunResult[];
}
export interface PipelineTaskOutcome {
  status: PipelineTaskStatus;
  fix_instructions: string | null;
  fixed_message: string | null;
  info: string | null;
  exc: string | null;
  should_continue: boolean;
  data: unknown;
}
export interface ResolverTokenCount {
  label: string;
  token_count: number;
}

export type ShopperReplyAgentBlueprintRaw =
  SnakeCasedPropertiesDeep<RcsMessageBlueprint>;

export interface ShopperReplyAgentOutput {
  final_message: string | null;
  route_type: ShopperAgentRouteType | null;
  coupon_id: number | null;
  failed_validation: boolean;
  attempts: number;
  step_results: PipelineTaskChainResult[];
  progress?: PipelineProgress | null;
  blueprint?: RcsMessageBlueprint;
  resolver_tokens?: ResolverTokenCount[];
  total_prompt_tokens?: number | null;
}

export interface ShopperReplyAgentOutputRaw
  extends Omit<ShopperReplyAgentOutput, 'blueprint'> {
  blueprint?: ShopperReplyAgentBlueprintRaw;
}

export function isStepResult(
  result: PipelineTaskChainResult,
  name: string,
): boolean {
  return result.name === name;
}

export function findStepResult(
  stepResults: PipelineTaskChainResult[],
  name: string,
): PipelineTaskChainResult | undefined {
  return stepResults.find((r) => r.name === name);
}

export type ShopperDebugAsyncStatusResponse =
  AsyncJobStatusResponse<ShopperReplyAgentOutput>;

export type ShopperDebugAsyncStatusResponseRaw =
  AsyncJobStatusResponse<ShopperReplyAgentOutputRaw>;

export interface PlaygroundMessageFaq {
  id: number;
  resourceType: string;
}

export interface PlaygroundMessageMetadata {
  faqs: PlaygroundMessageFaq[];
}

export interface ShopperWebchatConversationMessage {
  direction: MessageDirectionKeys;
  body: string;
  data?: string;
  created_at: string;
  blueprint?: RcsMessageBlueprint;
  debugData?: ShopperDebugAsyncStatusResponse;
  metadata?: PlaygroundMessageMetadata;
  isNoResponse?: boolean;
  feedbackSummary?: FeedbackSummary | null;
  messageId?: string;
  jobId?: string;
}

export interface ShopperAgentInputs {
  // Branding data
  shop_name: string;
  shop_domain: string;

  // BrandVoice Blurbs
  brand_guidelines?: string;
  industry?: string;
  target_customer_description?: string;

  // BrandVoice Persona
  persona_first_name?: string;
  persona_age?: string;
  persona_description?: string;
  persona_gender?: string;
  persona_location?: string;
  persona_self_identifier?: string;
  persona_title?: string;

  // BrandVoice Tone Preferences
  tone_content?: string;
  tone_formality?: string;
  tone_overall?: string;
  tone_verbosity?: string;

  // BrandVoice Emoji Preferences
  emoji_frequency?: string;
  allowed_emojis: string[];
  blocked_emojis: string[];

  // BrandVoice Words & Phrases Preferences
  preferred_greetings: string[];
  preferred_sign_offs: string[];
  preferred_words_and_phrases: string[];
  blocked_words_and_phrases: string[];
  admin_banned_words_and_phrases: string[];

  // Shopper Settings
  support_input?: string;
  persona_input?: string;

  // FAQ Data
  faq_input?: string;
  faq_document_input?: string;

  // Product data
  product_instructions?: string;
  product_data: Record<string, any>[];
  product_recommendations: Record<string, any>[];

  // Offers Data
  sales_offers?: string;
  offers: Record<string, any>[];
}

export interface ShopperWebchatConversation {
  shop_id: number;
  subscriber_id?: number;
  messages: ShopperWebchatConversationMessage[];
}

export interface ShopperReplyAgentSettings {
  shopper_prompt_id?: string;
  shopper_prompt_env?: string;
  supervisor_prompt_env?: string;
  max_attempts?: number;
  custom_shopper_prompt?: string;
  custom_prompt_inputs?: Record<string, any>;
  freeplay_enabled?: boolean;
  freeplay_record_pct?: number;
}

export interface PromptVariableOverride {
  key: string;
  value: string;
}

export interface ShopperWebchatRequest {
  conversation: ShopperWebchatConversation;
  prompt_inputs?: ShopperAgentInputs;
  agent_settings?: ShopperReplyAgentSettings;
  ssai_settings_overrides?: Record<string, any>;
}

export interface UserPlaygroundConversation {
  messages: ShopperWebchatConversationMessage[];
}

export interface UserPlaygroundRequest {
  conversation: UserPlaygroundConversation;
}

export interface UserPlaygroundAsyncResponse {
  job_id: string;
  status: string;
}

export interface UserPlaygroundMessageOutput {
  final_message: string;
  progress?: PipelineProgress | null;
}

export type UserPlaygroundStatusResponse =
  AsyncJobStatusResponse<UserPlaygroundMessageOutput>;
